import { useDebouncedCallback } from '@mantine/hooks';
import { EditorEvents } from '@tiptap/react';
import { DependencyList, useEffect, useState } from 'react';
import {
  calculateEditorState,
  CalculateEditorStateParams,
  EditorStateCalculation,
} from '../utility/editorUtilities';

/**
 * Future performance improvement todo:
 *
 * Depending on how often we end up re-using this hook for the
 * same editor across different components, it may be worthwhile
 * instead to setup a zustand store per editor and use that
 * to store the editor state, then use the zustand per editor hook
 * in the components that need to know about the editor state.
 *
 * However, do not put that state into a regular react context
 * that wraps all of the Editorial create/edit children
 * as that had caused too many re-renders to unrelated
 * components that do not need to know about the x editor state.
 *
 * We can use a similar pattern here, e.g. debouncedTransactionHandler
 * that updates the zustand stores per editor and attach them
 * somewhere high up in the component tree, like the AssetEditorContext...
 *
 * It's just important to not read from the state in that component
 * if we do instead move this over to that zustand + handler pattern
 * and we would need an effect to reset the stores on unmount as
 * well as initially run the store upon attaching the handler.
 *
 * also in this hook can to the useEffects, if db properly:
 * editor.on('transaction', dbUpdateEditorStateTxn);
 * but need to test the performance first
 *
 **/

// Note: some tiptap events contain transaction, some dont
// see: https://tiptap.dev/docs/editor/api/events
export type TiptapTransactionEvent = EditorEvents['transaction'];
export type TiptapCreateEvent = EditorEvents['create'];

// optional - can force hook rerun on deps change
// default will just subscribe to editor events onCreate/update
export interface UseCalcEditorStateProps extends CalculateEditorStateParams {
  deps?: DependencyList;
}

type ResultState = EditorStateCalculation | undefined;

export function useCalcEditorState({
  editor,
  isContentEditor = false,
  recordData,
  editorialConfig,
  deps = [],
}: UseCalcEditorStateProps): ResultState {
  // Setup initial state...
  const [editorStateCalc, setEditorStateCalc] = useState<ResultState>(() => {
    const result = calculateEditorState({
      editor,
      isContentEditor,
      recordData,
      editorialConfig,
    });
    return result;
  });

  const dbUpdateEditorStateManual = useDebouncedCallback(
    () => {
      if (!editor) {
        return;
      }

      const result = calculateEditorState({
        editor,
        isContentEditor,
        recordData,
        editorialConfig,
      });

      setEditorStateCalc(result);
    },
    {
      delay: 75,
      flushOnUnmount: false,
    },
  );

  const dbUpdateEditorStateTxn = useDebouncedCallback(
    (event: TiptapTransactionEvent) => {
      const result = calculateEditorState({
        editor: event?.editor,
        isContentEditor,
        recordData,
        editorialConfig,
      });
      setEditorStateCalc(result);
    },
    {
      delay: 200,
      flushOnUnmount: false,
    },
  );

  const dbUpdateEditorStateCreate = useDebouncedCallback(
    (event: TiptapCreateEvent) => {
      const result = calculateEditorState({
        editor: event?.editor,
        isContentEditor,
        recordData,
        editorialConfig,
      });
      setEditorStateCalc(result);
    },
    {
      delay: 100, // lower delay on create event
      flushOnUnmount: false,
    },
  );

  // effect 1: editor event-based calculation
  useEffect(() => {
    if (!editor) {
      return;
    }

    // register editor transaction listeners
    editor.on('create', dbUpdateEditorStateCreate);
    editor.on('update', dbUpdateEditorStateTxn);
    return () => {
      editor?.off('create', dbUpdateEditorStateCreate);
      editor?.off('update', dbUpdateEditorStateTxn);
    };
  }, [editor, dbUpdateEditorStateCreate, dbUpdateEditorStateTxn]);

  // effect 2: rerun calculation on params / custom deps change
  // VERY IMPORTANT: if needing to use the live record eg
  // the reactHookForm state, do NOT use watch() directly as its unstable
  // and will cause infinite loop here, you CAN use the useWatch() hook though
  // instead for recordData as it is stable and will not cause the looping issue
  // https://react-hook-form.com/docs/usewatch

  // eslint opt out:
  // Warning: "React Hook useEffect has a spread element in its dependency array."
  // This means we can't statically verify whether you've passed the correct dependencies.
  // TS compiler cant know what you are passing in during static checking, so unfortunately
  // there is no way to avoid this warning, must trust dev to pass stable deps if using deps prop.
  // opting out of eslint here, so verify rerenders manually if using custom deps.
  useEffect(() => {
    if (!editor) {
      return;
    }

    // Manually rerun the state calc if deps change
    dbUpdateEditorStateManual();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isContentEditor, recordData, editorialConfig, ...deps]);

  return editorStateCalc;
}
